import axios from 'axios';
import Log from '../../utils/log.js';
import { notifyConnectionError, setConnectionErrorCallback } from '../EventoServer/connectionError.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
const log = (method, url, data, parameters) => {
    Log.debug({
        WebRequest: `${method} ${url}`,
        Payload: data,
        Parameters: parameters,
    });
};
let failedCalls = 0;
/**
 * registro un interecettore per gli errori di rete
 * in modo da avere una notifica di fallimento significativa se
 * falliscono almeno due chiamate per motivi di rete
 * @param {*} err
 * @returns {Promise}
 */
function axiosErrorInterceptor(err) {
    //  niente risposta e non è un erroe axios deve essere un errore di rete
    // niente connessione o chiamate bloccate
    if (!!err.isAxiosError && !err.response && !err.status) {
        failedCalls += 1;
        if (failedCalls > 1) {
            notifyConnectionError('La comunicazione con il server è fallita. Verificare di non avere antivirus che blocchino le connessioni o problemi con la rete.');
        }
    }
    return Promise.reject(err);
}
axios.interceptors.response.use(null, axiosErrorInterceptor);
/**
 * Permette di trasformare un oggetto in una serie di parametri passati in querystring.
 * @param {object} parameters un oggetto javascript che specifica i parametri da passare in querystring
 * (per semplicità verrà anche eseguito l'encoding dei parametri passati prima di essere aggiunti in querystring)
 * @returns {string} url parameters
 */
const concatenateParameters = (parameters) => {
    const parametersList = [];
    if (parameters) {
        Object.entries(parameters).forEach(([paramName, value]) => {
            const values = Array.isArray(value) ? value : [value];
            for (let n = 0; n < values.length; n += 1) {
                parametersList.push(`${encodeURI(paramName)}=${values[n] == null ? '' : encodeURI(values[n])}`);
            }
        });
    }
    return parametersList.join('&');
};
/**
 * Permette di eseguire una chiamata AJAX in GET richiedendo la risposta in formato JSON.
 * Il risultato di questa chiamata è una Promise sulla richiesta perciò sarà possibile concatenare una azione mediante il metodo then.
 * @param {string} url l'url dell'endpoint da chiamare
 * @param {object} parameters un oggetto javascript che specifica i parametri da passare in querystring
 * (per semplicità verrà anche eseguito l'encoding dei parametri passati prima di essere aggiunti in querystring)
 * @param {boolean | undefined} enableCredentialWithCors I flag nella chiamata cors e withCredentials vengono automaticamente
 * impostati se l'url comincia con http. Questo parametro permette di forzare l'uso o meno di quei flag
 * @param {boolean=} [allResponse=false] se impostato a true (di default è false) fa ritornare alla funzione
 * l'intera response restituita dalla promise e non solamente la parte data
 */
const get = function get(url, parameters, enableCredentialWithCors, allResponse = false) {
    let cors = url.indexOf('http://') === 0 || url.indexOf('https://') === 0;
    if (enableCredentialWithCors != null) {
        cors = enableCredentialWithCors;
    }
    log('GET', url, '{}', parameters);
    return axios({
        method: 'get',
        params: parameters || {},
        url,
        headers: {
            'Content-Type': 'application/json',
        },
        withCredentials: cors,
    })
        .then((response) => (allResponse ? response : response.data));
};
/**
 * Permette di eseguire una chiamata AJAX di tipo POST e accetta come parametri.
 * Il risultato di questa chiamata è una Promise sulla richiesta perciò sarà possibile concatenare una azione mediante il metodo then.
 * @param {string} url l'url dell'endpoint da chiamare
 * @param {object} data l'oggetto da passare nel corpo della chiamata POST
 * @param {object} parameters un oggetto javascript che specifica i parametri da passare in querystring
 * (per semplicità verrà anche eseguito l'encoding dei parametri passati prima di essere aggiunti in querystring)
 * @param {boolean | undefined} enableCredentialWithCors I flag nella chiamata cors e withCredentials vengono automaticamente impostati se
 * l'url comincia con http. Questo parametro permette di forzare l'uso o meno di quei flag
 * @param {boolean=} [allResponse=false] se impostato a true (di default è false) fa ritornare alla funzione
 * l'intera response restituita dalla promise e non solamente la parte data
 * @returns {Promise}
 */
const post = function post(url, data, parameters, enableCredentialWithCors, allResponse = false) {
    let cors = url.indexOf('http://') === 0 || url.indexOf('https://') === 0;
    if (enableCredentialWithCors != null) {
        cors = enableCredentialWithCors;
    }
    log('POST', url, data, parameters);
    return axios({
        method: 'post',
        params: parameters || {},
        url,
        headers: {
            'Content-Type': 'application/json',
        },
        data: JSON.stringify(data),
        withCredentials: cors,
    })
        .then((response) => (allResponse ? response : response.data));
};
/**
 * Permette di eseguire una chiamata AJAX di tipo POST per inviare un file e accetta come parametri .
 * @param {string} url l'url dell'endpoint da chiamare
 * @param {object} formData il FormData da passare nel corpo della chiamata POST
 * @param {boolean | undefined} enableCors I flag nella chiamata cors e withCredentials vengono automaticamente impostati se l'url comincia con http.
 * Questo parametro permette di forzare l'uso o meno di quei flag
 * @param {func} onUploadProgress callback per seguire la progressione dell'upload (evento axios)
 * @param {func} onDownloadProgress callback per seguire la progressione del download (evento axios)
 * @returns {Promise}
 */
const postFile = function postFile(url, formData, enableCredentialWithCors, onUploadProgress, onDownloadProgress) {
    let cors = url.indexOf('http://') === 0 || url.indexOf('https://') === 0;
    if (enableCredentialWithCors != null) {
        cors = enableCredentialWithCors;
    }
    return axios({
        method: 'post',
        url,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        data: formData,
        withCredentials: cors,
        // `onUploadProgress` allows handling of progress events for uploads
        onUploadProgress(progressEvent) {
            // Do whatever you want with the native progress event
            onUploadProgress === null || onUploadProgress === void 0 ? void 0 : onUploadProgress(progressEvent);
        },
        // `onDownloadProgress` allows handling of progress events for downloads
        onDownloadProgress(progressEvent) {
            // Do whatever you want with the native progress event
            onDownloadProgress === null || onDownloadProgress === void 0 ? void 0 : onDownloadProgress(progressEvent);
        },
    });
};
/**
 * Permette di eseguire una chiamata AJAX in GET richiedendo un file:.
 * Il risultato di questa chiamata è una Promise sulla richiesta perciò sarà possibile concatenare una azione mediante il metodo then.
 * @param {string} url l'url dell'endpoint da chiamare
 * @param {object} params un oggetto javascript che specifica i parametri da passare in querystring
 * (per semplicità verrà anche eseguito l'encoding dei parametri passati prima di essere aggiunti in querystring)
 * @param {boolean | undefined} enableCredentialWithCors I flag nella chiamata cors e withCredentials vengono automaticamente
 * impostati se l'url comincia con http. Questo parametro permette di forzare l'uso o meno di quei flag
 * @param {boolean=} [allResponse=false] se impostato a true (di default è false) fa ritornare alla funzione
 * l'intera response restituita dalla promise e non solamente la parte data
 */
const getFile = function getFile(url, params, enableCredentialWithCors, allResponse = false) {
    const cors = enableCredentialWithCors != null ? enableCredentialWithCors : (url.indexOf('http://') === 0 || url.indexOf('https://') === 0);
    return axios({
        method: 'get',
        params: params || {},
        responseType: 'blob',
        url,
        withCredentials: cors,
    })
        .then((response) => (allResponse ? response : response.data));
};
/**
 * Funzione che ritorna il nome del file letto in GET.
 * @param {object} response response completa della chiamata getFile con il parametro allResponse=true.
 * @returns file name
 */
const getFileNameFromGetFileResponse = function getFileNameFromGetFileResponse(response) {
    if (!response || !response.headers || !response.headers['content-disposition'])
        return null;
    const regex = /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i;
    const matches = regex.exec(response.headers['content-disposition']);
    let filename = null;
    const matched = matches != null && (matches[3] || matches[2]);
    if (matched) {
        filename = matched.replace(/['"]/g, '');
    }
    return filename ? decodeURI(filename) : null;
};
/**
 * Metodo per impostare un header da passare a tutte le chiamate fatte con WebRequest.
 * @param {string} name nome dell'header da impostare
 * @param {string} value valore da impostare
 */
const setDefaultHeader = (name, value) => {
    axios.defaults.headers.common[name] = value;
};
const api = {
    concatenateParameters,
    getFileNameFromGetFileResponse,
    get,
    post,
    getFile,
    postFile,
    setDefaultHeader,
    setConnectionErrorCallback,
};

export { axiosErrorInterceptor, api as default };
